/**
 * A wrapper around the browser `fetch` API:
 * https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API/Using_Fetch
 */

import { CustomError } from 'types/helpers';
import { PlatformsEnum } from '@bondsports/types';
import { EAllSettledStatus, IAllSettledResponse } from 'types/network';
import { processVersionHeader } from '@bondsports/utils';

async function handleHttpResponse(response: Response) {
	const contentType = response.headers.get('content-type');

	const responseBody = contentType?.includes('application/json') ? await response.json() : await response.text();

	// Once we will have cognito logic the version header handling will be located inside the correct interceptor
	processVersionHeader(response);
	/**
	 * Handle a success response.
	 * Anything which isn't between 200-300 HTTP status will throw an error.
	 */
	if (response.status >= 200 && response.status < 300) {
		return responseBody;
	}

	const errorMsg = responseBody.error || response.statusText || response.status;
	const error: CustomError = new Error(errorMsg);

	error.meta = {
		body: responseBody,
		message: responseBody.error,
		statusCode: response.status,
		statusText: response.statusText,
	};

	throw error;
}

/**
 * @example
 * network.get('users/123');
 * network.get('users/123', { mode: 'no-cors' });
 * network.get('http://jsonplaceholder.typicode.com/users');
 */
async function get(path: string, options = {}) {
	validatePath(path);
	const response = await fetch(path, {
		method: 'GET',
		...options,
	});

	return handleHttpResponse(response);
}

/**
 * @example
 * network.post('users', { name: 'Frodo' });
 *
 * @example
 * network.post('users/123/setName', 'Frodo', {
 *   headers: {
 *     'Content-Type': 'text/plain'
 *   }
 * });
 */
async function post(path: string, data: unknown, headers = {}, options = {}) {
	validatePath(path);
	const response: Response = await fetch(path, {
		method: 'POST',
		body: JSON.stringify(data),
		headers: {
			'Content-Type': 'application/json',
			...headers,
		},
		...options,
	});

	return handleHttpResponse(response);
}

async function put(path: string, data: unknown, headers = {}, options = {}) {
	validatePath(path);
	const response: Response = await fetch(path, {
		method: 'PUT',
		body: JSON.stringify(data),
		headers: headers,
		...options,
	});

	return handleHttpResponse(response);
}

async function deleteMethod(path: string, data: unknown, headers = {}, options = {}) {
	validatePath(path);
	const response: Response = await fetch(path, {
		method: 'DELETE',
		body: JSON.stringify(data),
		headers: {
			'Content-Type': 'application/json',
			...headers,
		},
		...options,
	});

	return handleHttpResponse(response);
}

const validatePath = (path: string) => {
	const invalidWords = ['undefined', 'null', Number.NaN.toString()];
	const invalidPath = invalidWords.some(element => {
		if (path.includes(element)) {
			return true;
		}

		return false;
	});

	if (invalidPath) {
		const msg = `The following path = ${path} is invalid`;
		console.error(`msg`);
		throw msg;
	}
};

const addPlatformToBody = (body = {}) => {
	return { ...body, platform: PlatformsEnum.CONSUMER };
};

/**
 * Parses the results of a `Promise.allSettled` call
 * @param results - The results of a `Promise.allSettled` call
 * @returns An object with the fulfilled and rejected promises
 */
const parseAllSettledResponse = (results: PromiseSettledResult<unknown>[]): IAllSettledResponse<unknown> => {
	const response: IAllSettledResponse<unknown> = {
		fulfilled: [],
		rejected: [],
	};

	results.forEach(result => {
		if (result.status === EAllSettledStatus.FULFILLED) {
			response.fulfilled.push(result.value);
		} else {
			response.rejected.push(result.reason);
		}
	});

	return response;
};

/**
 * Downloads a file from a URL with custom headers and saves it with the specified filename
 *
 * Process:
 * 1. Makes a (potentially authenticated) GET request to the API to retrieve the file
 * 2. Creates a temporary Blob from the response
 * 3. Creates a temporary URL and link for the blob
 * 4. Triggers the download through the link
 * 5. Cleans up by removing the link and revoking the blob URL
 *
 * The blob handling is necessary because our api requires authentication.
 * Can't always simply redirect the browser to a file path.
 *
 * @param url - The URL to download the file from
 * @param filename - The name to give the downloaded file
 * @param headers - Optional headers object to include in the request
 * @param acceptType - Optional content type to expect in the response
 */
async function downloadFile(
	url: string,
	filename: string,
	headers: Record<string, unknown> = {},
	acceptType?: string
): Promise<void> {
	try {
		validatePath(url);
		const response = await fetch(url, {
			method: 'GET',
			headers: {
				...(acceptType && { Accept: acceptType }),
				...headers,
			},
		});

		if (!response.ok) {
			throw new Error(`HTTP error! status: ${response.status}`);
		}

		const blob = await response.blob();
		const downloadUrl = window.URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.style.display = 'none';
		link.href = downloadUrl;
		link.download = filename;
		document.body.appendChild(link);
		link.click();

		// Clean up on next tick
		setTimeout(() => {
			document.body.removeChild(link);
			window.URL.revokeObjectURL(downloadUrl);
		});
	} catch (error) {
		console.error('Error downloading file:', error);
		throw error;
	}
}

export const network = {
	get,
	post,
	put,
	delete: deleteMethod,
	addPlatformToBody,
	parseAllSettledResponse,
	downloadFile,
};
